import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Grip, TrendingDown } from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from 'recharts';

const TEAL = '#5BB5A2';
const TEAL_LIGHT = 'rgba(91,181,162,0.15)';
const TEAL_FUTURE = 'rgba(91,181,162,0.35)';
const DARK = '#1A1A1A';

const CATEGORIES = [
  {
    key: 'cardio',
    label: 'Cardio',
    alias: 'Engine',
    shortLabel: 'Cardio',
    prompt: 'How long can you sustain steady effort (run, cycle, brisk walk)?',
    scale: ['0 — Cannot sustain 5 min', '5 — 20-30 min comfortably', '10 — 60 min+ without issue'],
    mobileScale: ['< 5 min', '20-30 min', '60 min+'],
  },
  {
    key: 'maxStrength',
    label: 'Max Strength',
    alias: 'Force',
    shortLabel: 'Strength',
    prompt: 'How strong are you in low-rep compound lifts?',
    scale: ['0 — Never tested', '5 — Intermediate lifts', '10 — Advanced/competitive'],
    mobileScale: ['Untested', 'Intermediate', 'Advanced'],
  },
  {
    key: 'muscularEndurance',
    label: 'Muscular Endurance',
    alias: 'Repeatability',
    shortLabel: 'Endurance',
    prompt: 'How well can you sustain repeated effort (e.g. push-ups, squats)?',
    scale: ['0 — Fatigues very quickly', '5 — Moderate stamina', '10 — High rep capacity'],
    mobileScale: ['Low', 'Moderate', 'High'],
  },
  {
    key: 'relativeStrength',
    label: 'Relative Strength',
    alias: 'Control',
    shortLabel: 'Control',
    prompt: 'How well can you control and move your body (e.g. pull-ups, single-leg work)?',
    scale: ['0 — Struggles with bodyweight', '5 — Solid bodyweight control', '10 — Advanced calisthenics'],
    mobileScale: ['Struggles', 'Solid', 'Advanced'],
  },
  {
    key: 'mobility',
    label: 'Mobility',
    alias: 'Longevity Base',
    shortLabel: 'Mobility',
    prompt: 'How freely can your joints move without restriction or pain?',
    scale: ['0 — Major restrictions/pain', '5 — Some tightness', '10 — Full range, pain-free'],
    mobileScale: ['Restricted', 'Some tightness', 'Full range'],
  },
];

const GRIP_BENCHMARKS = [
  { level: 'Low', time: '< 20s', color: 'text-red-500' },
  { level: 'Average', time: '20–40s', color: 'text-orange-500' },
  { level: 'Good', time: '40–60s', color: 'text-[#C9A962]' },
  { level: 'Excellent', time: '60–120s', color: 'text-emerald-500' },
  { level: 'Elite', time: '120s+', color: 'text-green-600' },
];

function getProfileLabel(values) {
  const arr = CATEGORIES.map((c) => values[c.key]);
  const avg = arr.reduce((s, v) => s + v, 0) / arr.length;
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  const range = max - min;

  const lowestKey = CATEGORIES.reduce((a, b) => (values[a.key] <= values[b.key] ? a : b)).key;
  const highestKey = CATEGORIES.reduce((a, b) => (values[a.key] >= values[b.key] ? a : b)).key;

  // Balanced profiles (low range)
  if (range <= 2) {
    const balancedProfiles = [
      { check: avg >= 7, label: 'Durable All-Rounder' },
      { check: avg >= 4, label: 'Balanced' },
      { check: true, label: 'Balanced but Underdeveloped' },
    ];
    return { label: balancedProfiles.find((p) => p.check).label };
  }

  // Dominant / imbalanced profiles
  const dominantProfiles = [
    { check: lowestKey === 'mobility', label: 'High Performer, Weak Base' },
    { check: highestKey === 'cardio' && values.cardio - avg > 2, label: 'Engine Dominant' },
    { check: highestKey === 'maxStrength' && values.maxStrength - avg > 2, label: 'Strength Dominant' },
    { check: min <= 3 && max >= 7, label: 'Peaks & Valleys' },
    { check: true, label: 'Mixed Profile' },
  ];
  return { label: dominantProfiles.find((p) => p.check).label };
}

function getLongevityInsight(values) {
  const lowestCat = CATEGORIES.reduce((a, b) => (values[a.key] <= values[b.key] ? a : b));
  const highestCat = CATEGORIES.reduce((a, b) => (values[a.key] >= values[b.key] ? a : b));
  const arr = CATEGORIES.map((c) => values[c.key]);
  const avg = arr.reduce((s, v) => s + v, 0) / arr.length;

  if (avg >= 8 && Math.max(...arr) - Math.min(...arr) <= 2) {
    return `Your fitness profile is well-balanced and strong across all five areas. This is the foundation of lasting performance. Maintain consistency and keep challenging your weaker areas to stay durable.`;
  }

  return `Your current fitness shows strength in ${highestCat.label} (${highestCat.alias}), but your longevity is limited by ${lowestCat.label} (${lowestCat.alias}). Improving this area will help you maintain your performance, reduce injury risk, and build fitness that lasts well beyond your peak years.`;
}

function getFutureProjection(values) {
  const projected = {};
  CATEGORIES.forEach((c) => {
    const v = values[c.key];
    let decline;
    if (c.key === 'mobility') {
      decline = v <= 4 ? 0.45 : v <= 6 ? 0.30 : 0.18;
    } else if (c.key === 'cardio') {
      decline = v <= 4 ? 0.35 : v <= 6 ? 0.22 : 0.12;
    } else {
      decline = v <= 4 ? 0.38 : v <= 6 ? 0.25 : 0.15;
    }
    projected[c.key] = Math.max(1, Math.round(v * (1 - decline) * 10) / 10);
  });
  return projected;
}

function CustomSlider({ value, onChange, category }) {
  const pct = (value / 10) * 100;

  return (
    <div className="group" data-testid={`slider-${category.key}`}>
      <div className="flex items-baseline justify-between mb-1">
        <div>
          <span className="text-sm font-semibold text-[#1A1A1A] tracking-wide uppercase">{category.label}</span>
          <span className="text-xs text-[#6B6B6B] ml-2 hidden sm:inline">({category.alias})</span>
        </div>
        <span
          className="text-2xl font-bold tabular-nums transition-colors"
          style={{ color: value >= 7 ? TEAL : value >= 4 ? '#C9A962' : '#EF4444', fontFamily: "'Oswald', sans-serif" }}
        >
          {value}
        </span>
      </div>
      <p className="text-xs text-[#6B6B6B] mb-3">{category.prompt}</p>
      <div className="relative h-10 flex items-center">
        <div className="absolute w-full h-1 bg-[#E5E0DB] rounded-full" />
        <div
          className="absolute h-1 rounded-full transition-all duration-150"
          style={{ width: `${pct}%`, backgroundColor: TEAL }}
        />
        <input
          type="range"
          min="0"
          max="10"
          step="1"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="absolute w-full h-10 opacity-0 cursor-pointer z-10"
        />
        <div
          className="absolute w-5 h-5 rounded-full border-2 shadow-sm transition-all duration-150 pointer-events-none"
          style={{
            left: `calc(${pct}% - 10px)`,
            backgroundColor: 'white',
            borderColor: TEAL,
          }}
        />
      </div>
      {/* Desktop: full scale labels */}
      <div className="hidden sm:flex justify-between mt-1">
        {category.scale.map((s, i) => (
          <span key={i} className="text-[10px] text-[#9B9B9B]" style={{ maxWidth: i === 1 ? '40%' : '30%', textAlign: i === 0 ? 'left' : i === 1 ? 'center' : 'right' }}>
            {s}
          </span>
        ))}
      </div>
      {/* Mobile: short scale labels */}
      <div className="flex sm:hidden justify-between mt-1">
        {category.mobileScale.map((s, i) => (
          <span key={i} className="text-[10px] text-[#9B9B9B]">
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

function RadarResult({ values, futureValues, showFuture }) {
  const data = CATEGORIES.map((c) => ({
    axis: c.label,
    short: c.shortLabel,
    current: values[c.key],
    future: futureValues ? futureValues[c.key] : 0,
    fullMark: 10,
  }));

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div className="w-full" style={{ maxWidth: 480, margin: '0 auto' }}>
      <ResponsiveContainer width="100%" height={isMobile ? 300 : 380}>
        <RadarChart data={data} cx="50%" cy="50%" outerRadius={isMobile ? '60%' : '75%'}>
          <PolarGrid stroke="#E5E0DB" />
          <PolarAngleAxis
            dataKey={isMobile ? 'short' : 'axis'}
            tick={{ fill: DARK, fontSize: isMobile ? 11 : 12, fontWeight: 600 }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 10]}
            tickCount={6}
            tick={{ fontSize: 10, fill: '#9B9B9B' }}
            axisLine={false}
          />
          {showFuture && futureValues && (
            <Radar
              name="Future"
              dataKey="future"
              stroke="rgba(239,68,68,0.5)"
              fill="rgba(239,68,68,0.08)"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={false}
            />
          )}
          <Radar
            name="Current"
            dataKey="current"
            stroke={TEAL}
            fill={TEAL_LIGHT}
            strokeWidth={2}
            dot={{ r: 4, fill: TEAL, stroke: 'white', strokeWidth: 2 }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function LongevityAssessment() {
  const [values, setValues] = useState({
    cardio: 5,
    maxStrength: 5,
    muscularEndurance: 5,
    relativeStrength: 5,
    mobility: 5,
  });
  const [submitted, setSubmitted] = useState(false);
  const [showFuture, setShowFuture] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const resultsRef = useRef(null);
  const assessmentRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  const handleSliderChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleGenerate = () => {
    setSubmitted(true);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleStartAssessment = () => {
    assessmentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const profile = submitted ? getProfileLabel(values) : null;
  const insight = submitted ? getLongevityInsight(values) : null;
  const futureValues = submitted ? getFutureProjection(values) : null;
  const arr = CATEGORIES.map((c) => values[c.key]);
  const avgScore = arr.reduce((s, v) => s + v, 0) / arr.length;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* ─── HERO ─── */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#1A1A1A] overflow-hidden">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(${TEAL} 1px, transparent 1px), linear-gradient(90deg, ${TEAL} 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
        <div className={`container relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link to="/resources" className="inline-flex items-center gap-2 text-[#C9A962] hover:text-white transition-colors mb-8" data-testid="back-to-resources">
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back to Resources</span>
          </Link>
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-[0.15em] uppercase mb-4 block" style={{ color: TEAL }}>
              Longevity Assessment
            </span>
            <h1 className="heading-display text-white mb-6">
              Train for the fitness<br />you can keep.
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mb-10">
              Discover your Longevity Polygon and see what supports — or limits — your long-term strength, mobility, and performance.
            </p>
            <button
              onClick={handleStartAssessment}
              data-testid="start-assessment-btn"
              className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold tracking-[0.1em] uppercase transition-all duration-300 hover:translate-y-[-2px]"
              style={{ backgroundColor: TEAL, color: DARK }}
            >
              Start Assessment
              <ChevronDown size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ─── INTRO ─── */}
      <section className="py-16 md:py-24 border-b border-[#E5E0DB]">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Most people train for short-term results. This assessment shows how balanced and durable your fitness is across the five qualities that matter most for longevity.
            </p>
            <p className="mt-4 text-sm font-medium" style={{ color: TEAL }}>
              "We're not building your peak. We're building what you can keep."
            </p>
          </div>
        </div>
      </section>

      {/* ─── ASSESSMENT ─── */}
      <section ref={assessmentRef} className="py-16 md:py-24 scroll-mt-24">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="mb-10">
              <h2 className="heading-medium mb-2">Rate Your Fitness</h2>
              <p className="text-sm text-[#6B6B6B]">Move each slider to rate yourself honestly from 0 to 10.</p>
            </div>

            <div className="space-y-10">
              {CATEGORIES.map((cat) => (
                <CustomSlider
                  key={cat.key}
                  category={cat}
                  value={values[cat.key]}
                  onChange={(val) => handleSliderChange(cat.key, val)}
                />
              ))}
            </div>

            <div className="mt-12 text-center">
              <button
                onClick={handleGenerate}
                data-testid="generate-polygon-btn"
                className="px-10 py-4 text-sm font-semibold tracking-[0.1em] uppercase transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg"
                style={{ backgroundColor: TEAL, color: 'white' }}
              >
                Generate My Polygon
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── RESULTS ─── */}
      {submitted && (
        <section
          ref={resultsRef}
          className="py-16 md:py-24 bg-white border-t border-[#E5E0DB] scroll-mt-8"
          style={{ animation: 'fadeInUp 0.6s ease-out' }}
          data-testid="results-section"
        >
          <div className="container">
            <div className="max-w-3xl mx-auto">
              {/* Header */}
              <div className="text-center mb-12">
                <span className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: TEAL }}>Your Results</span>
                <h2 className="heading-medium mt-2 mb-1">Your Longevity Polygon</h2>
              </div>

              {/* Radar Chart */}
              <RadarResult values={values} futureValues={futureValues} showFuture={showFuture} />

              {/* Future Projection Toggle */}
              <div className="flex items-center justify-center gap-3 mt-4 mb-10">
                <button
                  onClick={() => setShowFuture(!showFuture)}
                  data-testid="future-toggle"
                  className="flex items-center gap-2 text-sm font-medium transition-colors"
                  style={{ color: showFuture ? TEAL : '#9B9B9B' }}
                >
                  <div
                    className="w-10 h-5 rounded-full flex items-center px-0.5 transition-colors duration-300 cursor-pointer"
                    style={{ backgroundColor: showFuture ? TEAL : '#E5E0DB' }}
                  >
                    <div
                      className="w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-300"
                      style={{ transform: showFuture ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </div>
                  <TrendingDown size={14} />
                  Show Future Projection
                </button>
              </div>

              {showFuture && (
                <p className="text-xs text-center text-[#9B9B9B] mb-8 -mt-4">
                  The dashed red outline shows estimated decline over 10+ years without targeted training. Weaker areas degrade faster.
                </p>
              )}

              {/* Profile Label + Score */}
              <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-10">
                <div className="bg-[#FAFAF8] p-4 sm:p-6 text-center" data-testid="profile-label">
                  <p className="text-xs text-[#9B9B9B] uppercase tracking-wide mb-1">Your Profile</p>
                  <p className="text-base sm:text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {profile.label}
                  </p>
                </div>
                <div className="bg-[#FAFAF8] p-4 sm:p-6 text-center" data-testid="avg-score">
                  <p className="text-xs text-[#9B9B9B] uppercase tracking-wide mb-1">Average Score</p>
                  <p className="text-base sm:text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif", color: avgScore >= 7 ? TEAL : avgScore >= 4 ? '#C9A962' : '#EF4444' }}>
                    {avgScore.toFixed(1)} / 10
                  </p>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="bg-[#FAFAF8] p-4 sm:p-6 mb-10">
                <p className="text-xs text-[#9B9B9B] uppercase tracking-wide mb-4">Score Breakdown</p>
                <div className="space-y-3">
                  {CATEGORIES.map((cat) => {
                    const v = values[cat.key];
                    const fv = futureValues ? futureValues[cat.key] : v;
                    return (
                      <div key={cat.key} className="flex items-center gap-2 sm:gap-3">
                        <span className="text-[11px] sm:text-xs font-semibold text-[#1A1A1A] w-20 sm:w-32 shrink-0 uppercase tracking-wide">
                          <span className="hidden sm:inline">{cat.label}</span>
                          <span className="sm:hidden">{cat.shortLabel}</span>
                        </span>
                        <div className="flex-1 h-2 bg-[#E5E0DB] rounded-full overflow-hidden relative">
                          {showFuture && (
                            <div
                              className="absolute h-full rounded-full opacity-30"
                              style={{ width: `${(fv / 10) * 100}%`, backgroundColor: '#EF4444' }}
                            />
                          )}
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${(v / 10) * 100}%`, backgroundColor: TEAL }}
                          />
                        </div>
                        <span className="text-sm font-bold tabular-nums w-6 text-right" style={{ color: TEAL }}>
                          {v}
                        </span>
                        {showFuture && (
                          <span className="text-xs tabular-nums text-red-400 w-8 sm:w-10 text-right">
                            ({fv.toFixed(0)})
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Longevity Insight */}
              <div className="border-l-2 pl-6 mb-10" style={{ borderColor: TEAL }} data-testid="longevity-insight">
                <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: TEAL }}>Longevity Insight</p>
                <p className="text-sm text-[#6B6B6B] leading-relaxed">{insight}</p>
              </div>

              {/* Grip Strength Block */}
              <div className="bg-[#1A1A1A] p-6 sm:p-8 mb-10" data-testid="grip-strength-block">
                <div className="flex items-center gap-3 mb-4">
                  <Grip className="shrink-0" style={{ color: TEAL }} size={22} />
                  <div>
                    <p className="text-white text-sm font-semibold">Longevity Marker: Grip Strength</p>
                    <p className="text-white/50 text-xs">Research links grip strength to cardiovascular health, mortality risk, and overall longevity.</p>
                  </div>
                </div>
                <p className="text-white/70 text-xs sm:text-sm mb-4">
                  Test yourself: How long can you hold a dead hang from a pull-up bar?
                </p>
                <div className="grid grid-cols-5 gap-1 sm:gap-2">
                  {GRIP_BENCHMARKS.map((b) => (
                    <div key={b.level} className="text-center">
                      <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wide mb-1">{b.level}</p>
                      <p className={`text-xs sm:text-sm font-semibold ${b.color}`}>{b.time}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="text-center py-12 border-t border-[#E5E0DB]" data-testid="results-cta">
                <h3 className="heading-medium mb-3">Want to improve your longevity profile?</h3>
                <p className="text-sm text-[#6B6B6B] mb-8 max-w-md mx-auto">
                  Get a personalised plan to build strength, mobility, and fitness that lasts.
                </p>
                <Link
                  to="/contact"
                  className="inline-block px-10 py-4 text-sm font-semibold tracking-[0.1em] uppercase transition-all duration-300 hover:translate-y-[-2px]"
                  style={{ backgroundColor: TEAL, color: 'white' }}
                  data-testid="book-consultation-btn"
                >
                  Book a Coaching Consultation
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Inline keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
